/**
 * Almacenamiento del servidor (variante tarjetas).
 *
 * Una sola sección para lo que antes estaba partido entre la pestaña
 * «Almacenamiento» (volúmenes) y el panel «Discos»: discos físicos, RAID y
 * volúmenes. La edición del inventario sigue en `ServerDisksPanel`; los
 * volúmenes y LUNs se montan debajo, en la misma pestaña.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

function fmtGb(value) {
  const gb = Number(value);
  if (!Number.isFinite(gb) || gb <= 0) return '—';
  if (gb >= 1024) return `${(gb / 1024).toLocaleString('es-ES', { maximumFractionDigits: 2 })} TB`;
  return `${gb.toLocaleString('es-ES', { maximumFractionDigits: 0 })} GB`;
}

function usoVolumen(v) {
  const total = Number(v.size_gb);
  const usado = Number(v.used_gb);
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(usado)) return null;
  return Math.max(0, Math.min(100, Math.round((usado / total) * 100)));
}

export default function ServerCardsStorage({ serverId, companyId, onOpenDisks }) {
  const [discos, setDiscos] = useState([]);
  const [raids, setRaids] = useState([]);
  const [controladoras, setControladoras] = useState([]);
  const [volumenes, setVolumenes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    if (!serverId || !companyId) return;
    setCargando(true);
    setError('');
    try {
      const [sr, vr] = await Promise.all([
        api.getServerStorage(serverId, companyId),
        api.getVolumes(companyId, 'rack_element', serverId),
      ]);
      const rec = sr?.data || {};
      const data = rec.data && typeof rec.data === 'object' ? rec.data : {};
      setDiscos(Array.isArray(data.physical_disks) ? data.physical_disks : []);
      setRaids([
        ...(Array.isArray(data.raid_hw) ? data.raid_hw : []),
        ...(Array.isArray(data.raid_sw) ? data.raid_sw : []),
      ]);
      setControladoras(Array.isArray(data.controllers) ? data.controllers : []);
      setVolumenes(Array.isArray(vr?.data) ? vr.data : []);
    } catch (e) {
      setError(e.message || 'No se pudo cargar el almacenamiento');
    } finally {
      setCargando(false);
    }
  }, [serverId, companyId]);

  useEffect(() => { cargar(); }, [cargar]);

  const capacidadTotal = useMemo(
    () => discos.reduce((acc, d) => acc + (Number(d.capacity_gb) || 0), 0),
    [discos],
  );

  if (cargando) {
    return <div className="sf2-card"><div className="sf2-empty">Cargando almacenamiento…</div></div>;
  }

  return (
    <div className="sf2-summary">
      <div className="sf2-card">
        <h3>
          Almacenamiento
          {onOpenDisks && (
            <button type="button" className="sf2-link" onClick={onOpenDisks}>Editar discos y RAID</button>
          )}
        </h3>
        <div className="sf2-hl">
          <div className="sf2-kpi"><span>Discos físicos</span><b>{discos.length}</b></div>
          <div className="sf2-kpi"><span>Capacidad bruta</span><b>{fmtGb(capacidadTotal)}</b></div>
          <div className="sf2-kpi"><span>Arrays RAID</span><b>{raids.length}</b></div>
          <div className="sf2-kpi"><span>Volúmenes</span><b>{volumenes.length}</b></div>
        </div>
        {error && <div className="sf2-empty" style={{ paddingTop: 10 }}>{error}</div>}
      </div>

      <div className="sf2-card">
        <h3>Discos físicos<span className="sf2-count">{discos.length}</span></h3>
        {discos.length === 0 ? (
          <div className="sf2-empty">Sin discos inventariados.</div>
        ) : (
          <div className="sf2-table-wrap">
            <table className="sf2-table">
              <thead>
                <tr><th>Bahía</th><th>Modelo</th><th>Capacidad</th><th>Interfaz</th><th>Estado</th></tr>
              </thead>
              <tbody>
                {discos.map((d, index) => {
                  const smart = d.health && typeof d.health === 'object' ? d.health : null;
                  const estado = smart?.smart_status
                    || (typeof d.health === 'string' ? d.health : '')
                    || d.status
                    || '—';
                  const tooltip = smart
                    ? [
                        smart.power_on_hours != null ? `${smart.power_on_hours} h` : null,
                        smart.temperature_c != null ? `${smart.temperature_c} °C` : null,
                      ].filter(Boolean).join(' · ')
                    : undefined;
                  return (
                    <tr key={d.slot || d.serial || index}>
                      <td>{d.slot || `Disco ${index + 1}`}</td>
                      <td>{[d.manufacturer, d.model].filter(Boolean).join(' ') || '—'}</td>
                      <td>{fmtGb(d.capacity_gb)}</td>
                      <td>{[d.interface, d.disk_type].filter(Boolean).join(' · ') || '—'}</td>
                      <td>
                        <span
                          className={`sf2-pill${/ok|online|good|healthy/i.test(String(estado)) ? ' sf2-pill--ok' : ''}`}
                          title={tooltip || undefined}
                        >
                          {estado}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {controladoras.length > 0 && (
          <div className="sf2-hint" style={{ marginTop: 10 }}>
            {controladoras.length} controladora{controladoras.length === 1 ? '' : 's'}: {controladoras.map((c) => c.model || 'sin modelo').join(', ')}
          </div>
        )}
      </div>

      <div className="sf2-card">
        <h3>RAID<span className="sf2-count">{raids.length}</span></h3>
        {raids.length === 0 ? (
          <div className="sf2-empty">Sin arrays RAID declarados.</div>
        ) : (
          <div className="sf2-rows">
            {raids.map((r, index) => (
              <div className="sf2-row" key={`${r.name || r.array_id || 'raid'}-${index}`}>
                <div className="sf2-row-main">
                  <b>{r.name || r.array_id || `Array ${index + 1}`} · {r.raid_type || r.level || 'RAID'}</b>
                  <small>
                    {[
                      Array.isArray(r.disk_slots) ? `${r.disk_slots.length} discos` : null,
                      r.total_gb ? `${fmtGb(r.total_gb)} total` : null,
                      r.usable_gb ? `${fmtGb(r.usable_gb)} útil` : null,
                      r.cache_policy,
                    ].filter(Boolean).join(' · ') || 'Sin detalle'}
                  </small>
                </div>
                <span className={`sf2-pill${/optimal|ok/i.test(r.status || '') ? ' sf2-pill--ok' : ''}`}>{r.status || '—'}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="sf2-card">
        <h3>Volúmenes<span className="sf2-count">{volumenes.length}</span></h3>
        {volumenes.length === 0 ? (
          <div className="sf2-empty">Sin volúmenes documentados.</div>
        ) : (
          <div className="sf2-rows">
            {volumenes.map((v) => {
              const pct = usoVolumen(v);
              return (
                <div className="sf2-row" key={v.id || `${v.mount_point}-${v.label}`}>
                  <div className="sf2-row-main">
                    <b>{v.mount_point || v.label || 'Volumen'}{v.label && v.mount_point ? ` · ${v.label}` : ''}</b>
                    <small>
                      {[v.filesystem, fmtGb(v.size_gb), v.protocol ? v.protocol.toUpperCase() : null]
                        .filter(Boolean).join(' · ') || 'Sin detalle'}
                    </small>
                  </div>
                  {pct != null ? (
                    <div className="sf2-vol-use">
                      <div className="sf2-bar"><i style={{ width: `${pct}%`, background: pct >= 85 ? '#d97706' : undefined }} /></div>
                      <span>{pct}%</span>
                    </div>
                  ) : (
                    <span className="sf2-pill">{v.status || '—'}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
